class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._openTimeoutId = null;
    this._renderTimeoutId = null;
    this._closedDuringOpen = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Detect bfcache restoration and reset drawer state
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this._resetDrawerState();
      }
    });

    // Safety net: detect and remove stale overflow-hidden when UpCart is active
    if (this._isUpCartActive()) {
      this._scrollLockWatchdog();
    }
  }

  _scrollLockWatchdog() {
    let debounceTimer = null;
    const observer = new MutationObserver(() => {
      if (!document.body.classList.contains('overflow-hidden')) return;
      if (this.classList.contains('active')) return;
      if (document.querySelector('modal-dialog[open], details-modal[open]')) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (
          document.body.classList.contains('overflow-hidden') &&
          !this.classList.contains('active') &&
          !document.querySelector('modal-dialog[open], details-modal[open]')
        ) {
          document.body.classList.remove('overflow-hidden');
          document.body.style.overflow = '';
        }
      }, 300);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (window.UpCart && typeof window.UpCart.openCart === 'function') {
        window.UpCart.openCart();
      } else {
        this.open(cartLink);
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        if (window.UpCart && typeof window.UpCart.openCart === 'function') {
          window.UpCart.openCart();
        } else {
          this.open(cartLink);
        }
      }
    });
  }

  open(triggeredBy) {
    // When UpCart is active, it owns the cart drawer and scroll locking
    if (this._isUpCartActive()) return;

    this._closedDuringOpen = false;

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this._openTimeoutId = setTimeout(() => {
      this._openTimeoutId = null;
      if (this._closedDuringOpen) return;
      this.classList.add('animate', 'active');
    });

    // Remove any stale transitionend handler before registering a new one
    if (this._openTransitionHandler) {
      this.removeEventListener('transitionend', this._openTransitionHandler);
    }

    this._openTransitionHandler = () => {
      // Only trap focus if the drawer is still active (not closed during transition)
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    this.addEventListener('transitionend', this._openTransitionHandler, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Prevent any pending open/render timeouts from re-adding classes after close
    this._closedDuringOpen = true;

    if (this._renderTimeoutId) {
      clearTimeout(this._renderTimeoutId);
      this._renderTimeoutId = null;
    }
    if (this._openTimeoutId) {
      clearTimeout(this._openTimeoutId);
      this._openTimeoutId = null;
    }

    // Remove overflow-hidden FIRST to restore page scrolling immediately
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';

    // Cancel any pending transitionend handler from open() to prevent race condition
    if (this._openTransitionHandler) {
      this.removeEventListener('transitionend', this._openTransitionHandler);
      this._openTransitionHandler = null;
    }

    // Remove both active and animate classes
    this.classList.remove('active', 'animate');

    removeTrapFocus(this.activeElement);
    this.activeElement = null;
  }

  _resetDrawerState() {
    this._closedDuringOpen = true;

    if (this._renderTimeoutId) {
      clearTimeout(this._renderTimeoutId);
      this._renderTimeoutId = null;
    }
    if (this._openTimeoutId) {
      clearTimeout(this._openTimeoutId);
      this._openTimeoutId = null;
    }

    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';

    if (this._openTransitionHandler) {
      this.removeEventListener('transitionend', this._openTransitionHandler);
      this._openTransitionHandler = null;
    }

    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    this.activeElement = null;
  }

  _isUpCartActive() {
    return document.body.hasAttribute('data-upcart-cart-enabled') ||
           !!(window.UpCart && typeof window.UpCart.openCart === 'function');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Only open the native drawer if UpCart is NOT active
    if (!this._isUpCartActive()) {
      this._renderTimeoutId = setTimeout(() => {
        this._renderTimeoutId = null;
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
